import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const SIZES = ['S', 'M', 'L', 'XL'];

function getSizeStock(product, size) {
  if (product.hasSize) {
    return typeof product.stock === 'object' && product.stock !== null
      ? (product.stock[size] ?? 0)
      : 0;
  }
  return typeof product.stock === 'number' ? product.stock : 0;
}

// SIZE BUTTON — extracted so hover styles are easy to manage with state
function SizeButton({ label, disabled, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={() => !disabled && onClick(label)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      title={disabled ? 'Out of stock' : undefined}
      style={{
        width: '54px',
        height: '54px',
        border: hovered && !disabled ? '2px solid #111' : '1px solid #ddd',
        background: hovered && !disabled ? '#111' : disabled ? '#f4f4f4' : 'white',
        color: hovered && !disabled ? 'white' : disabled ? '#bbb' : '#111',
        fontWeight: 700,
        fontSize: '0.85rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: '2px',
        transition: 'all 0.12s ease',
        flexShrink: 0,
        textDecoration: disabled ? 'line-through' : 'none',
      }}
    >
      {label}
    </button>
  );
}

function QuickAddModal({ product, onClose }) {
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState(null);
  const [added, setAdded] = useState(false);

  const isOutOfStock = product.hasSize
    ? (typeof product.stock !== 'object' || !Object.values(product.stock).some(v => v > 0))
    : (product.stock === 0);

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // For sized products: pick a size → add to cart → confirm → close
  function handleSizeSelect(size) {
    if (added) return;
    addToCart(product, size, null, 1);
    setSelectedSize(size);
    setAdded(true);
    setTimeout(onClose, 1400);
  }

  // For no-size products: single "Add to Cart" button
  function handleNoSizeAdd() {
    if (added) return;
    addToCart(product, null, null, 1);
    setAdded(true);
    setTimeout(onClose, 1400);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  const mainImg = product.images?.[0] || product.imageUrl || 'https://placehold.co/64x64/f4f4f4/888?text=PC';

  return ReactDOM.createPortal(
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '1rem'
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '2px',
          width: '100%',
          maxWidth: '380px',
          padding: '1.75rem',
          position: 'relative',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: '0.75rem', right: '0.75rem',
            background: 'none', border: 'none', fontSize: '1.4rem',
            cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '0.2rem 0.4rem'
          }}
        >
          &times;
        </button>

        {/* Product summary */}
        <div className="d-flex gap-3 align-items-center mb-4">
          <img
            src={mainImg}
            alt={product.name}
            style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }}
            onError={e => { e.target.src = 'https://placehold.co/64x64/f4f4f4/888?text=PC'; }}
          />
          <div>
            <h6 className="fw-bold mb-1" style={{ lineHeight: 1.3, marginRight: '1.5rem' }}>
              {product.name}
            </h6>
            <p className="mb-0 fw-semibold" style={{ color: '#111' }}>
              ${product.price.toFixed(2)}
            </p>
          </div>
        </div>

        {/* ---- Main content ---- */}
        {added ? (
          <div className="text-center py-3">
            <p className="fw-bold mb-1" style={{ color: '#2e7d32', fontSize: '1.15rem', letterSpacing: '0.02em' }}>
              ✓ Added to Cart
            </p>
            <p className="small text-muted mb-3">
              {product.name}{selectedSize ? ` — Size ${selectedSize}` : ''}
            </p>
            <Link to="/cart" className="small fw-semibold text-decoration-none"
              style={{ color: '#c84b11' }} onClick={onClose}>
              View Cart →
            </Link>
          </div>

        ) : isOutOfStock ? (
          <p className="text-muted text-center py-2 mb-0 small">This item is out of stock.</p>

        ) : product.hasSize ? (
          /* Sized product — pick a size */
          <>
            <p className="small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.1em', color: '#111' }}>
              Select Size
            </p>
            <div className="d-flex gap-2 flex-wrap mb-4">
              {SIZES.map(size => (
                <SizeButton
                  key={size}
                  label={size}
                  disabled={getSizeStock(product, size) === 0}
                  onClick={handleSizeSelect}
                />
              ))}
            </div>
            <p className="small text-muted mb-0 text-center">
              Or{' '}
              <Link to={`/product/${product.id}`} className="text-decoration-none fw-semibold"
                style={{ color: '#111' }} onClick={onClose}>
                view full details
              </Link>
            </p>
          </>

        ) : (
          /* No-size product — direct Add to Cart */
          <>
            <button
              className="btn btn-piggy w-100 py-2 mb-3"
              onClick={handleNoSizeAdd}
            >
              Add to Cart
            </button>
            <p className="small text-muted mb-0 text-center">
              Or{' '}
              <Link to={`/product/${product.id}`} className="text-decoration-none fw-semibold"
                style={{ color: '#111' }} onClick={onClose}>
                view full details
              </Link>
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default QuickAddModal;
